import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import { useBackgroundTheme } from '@/hooks/useBackgroundTheme';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import type { SearchOffersDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { isLightBackground } from '@/utils/color';
import { formatDateLLL } from '@/utils/dateTime';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import { v4 } from 'uuid';

interface JourneysNotFoundProps {
  outboundData: SearchOffersDTO;
  inboundData: SearchOffersDTO;
}

export const JourneysNotFound: FC<JourneysNotFoundProps> = ({
  outboundData: {
    journeys: outboundJourneys,
    nextAvailableDepartureDate: outboundNextAvailableDepartureDate,
  },
  inboundData: {
    journeys: inboundJourneys,
    nextAvailableDepartureDate: inboundNextAvailableDepartureDate,
  },
}) => {
  const backgroundTheme = useBackgroundTheme();
  const isLight = isLightBackground(backgroundTheme);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { setIsSearchOverlayOpened, currentSearchFormValues, handleSubmit } =
    useSearchWidget();

  if (
    (!outboundJourneys || outboundJourneys.length > 0) &&
    (!inboundJourneys || inboundJourneys.length > 0)
  ) {
    return null;
  }

  const renderNextAvailableDateButtonText = () => {
    if (
      outboundNextAvailableDepartureDate &&
      inboundNextAvailableDepartureDate
    ) {
      return (
        <TransText
          i18nKey="nextAvailableDates"
          values={{
            departureDate: formatDateLLL(
              new Date(outboundNextAvailableDepartureDate)
            ),
            returnDate: formatDateLLL(
              new Date(inboundNextAvailableDepartureDate)
            ),
          }}
        />
      );
    } else if (outboundNextAvailableDepartureDate) {
      return (
        <TransText
          i18nKey="nextAvailableDepartureDate"
          values={{
            date: formatDateLLL(new Date(outboundNextAvailableDepartureDate)),
          }}
        />
      );
    } else if (inboundNextAvailableDepartureDate) {
      return (
        <TransText
          i18nKey="nextAvailableReturnDate"
          values={{
            date: formatDateLLL(new Date(inboundNextAvailableDepartureDate)),
          }}
        />
      );
    }
  };

  return (
    <div className="mx-auto w-fit px-4 py-8 laptop:px-0">
      <Typography
        className={isLight ? 'text-neutral' : 'text-neutral-light'}
        variant="paragraph"
        data-testid="no-trips-found-message"
      >
        <TransText i18nKey="noTripsFoundPleaseChangeSearchInputsAndTryAgain" />
      </Typography>
      {(outboundNextAvailableDepartureDate ||
        inboundNextAvailableDepartureDate) && (
        <Button
          data-testid="next-available-date-btn"
          variant="secondary"
          size="large"
          className="mt-8"
          fullWidth
          onClick={() => {
            if (currentSearchFormValues) {
              handleSubmit({
                ...currentSearchFormValues,
                ...(outboundNextAvailableDepartureDate && {
                  departureDate: new Date(outboundNextAvailableDepartureDate),
                }),
                ...(inboundNextAvailableDepartureDate && {
                  returnDate: new Date(inboundNextAvailableDepartureDate),
                }),
                passengers: currentSearchFormValues.passengers.map((p) => ({
                  ...p,
                  externalReference: v4(),
                })),
              });
            }
          }}
        >
          {renderNextAvailableDateButtonText()}
          <Icons.arrowRight height={20} width={20} />
        </Button>
      )}
      {!isLaptopOrBigger && (
        <Button
          fullWidth
          size="large"
          onClick={() => setIsSearchOverlayOpened(true)}
          variant="secondary"
          data-testid="edit-search-params-btn"
          className="mt-6"
        >
          <Icons.search height={20} width={20} />
          <TransText i18nKey="editSearch" />
        </Button>
      )}
    </div>
  );
};
